import {FC, useState} from 'react'
import {useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {KTSVG} from '../../../../../../template/helpers'
import {appsAPI} from '../../../../../../services/AppsService'
import {IAppsModel} from '../../../../../../types/apps/AppsModel'
import {Bitrix24UserInServiceInitValues} from '../../../../core/_models'
import {IBitrix24UserInServiceModel} from '../../../../../../types/apps/crm/biitrix24/BitrixUserInServiceModel'
import {useIntl} from 'react-intl'

type Props = {
  user: IBitrix24UserInServiceModel
}

// fullname is not required when the app does not keep employees personal data (docs/PERSONAL_DATA.md)
const getBitrix24UsersInServiceEditShema = (personalData: boolean) =>
  Yup.object().shape({
    is_active: Yup.boolean(),
    internal_number: Yup.string(),
    external_number: Yup.string(),
    bitrix24_user_id: Yup.number(),
    fullname: personalData ? Yup.string().required('Name is required') : Yup.string(),
    bitrix24_manager_time: Yup.boolean(),
    smart_call: Yup.boolean(),
    chat: Yup.boolean(),
    auto_answer: Yup.boolean(),
    multiple_registration: Yup.boolean(),
  })

const OneUserInServiceBitrix24: FC<Props> = ({user}) => {
  const intl = useIntl()
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const [userState, updateUserState] = useState(false)
  const [smartCallState, updateSmartCallState] = useState(user.smart_call)
  const [b24TimeState, updateb24TimeState] = useState(user.bitrix24_manager_time)
  const [timeState, updateTimeState] = useState(
    user.smart_call === true && user.bitrix24_manager_time === false ? false : true
  )
  const [b24TimeDisabledState, updateb24TimeDisabledState] = useState(!user.smart_call)
  const [updateApp, {isLoading}] = appsAPI.useUpdateAppMutation()
  const all_users = app?.settings.bitrix24.users_in_service_list
  const personalData = app?.personal_data === true

  const checkSMCallDisabled = (checked: boolean) => {
    updateSmartCallState(checked)
    const b24Time = document.getElementById('userEditB24Time' + user.bitrix24_user_id)
    if (smartCallState === true) {
      if (b24TimeState === true) {
        b24Time?.click()
        updateb24TimeState(false)
      }
      updateb24TimeDisabledState(true)
      updateTimeState(true)
    } else if (smartCallState === false) {
      updateb24TimeDisabledState(false)
      updateTimeState(false)
    }
  }

  const validateNumber = (number: string) => {
    const alph = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '+']
    let mark = false
    let new_number = ''
    for (let j = 0; j < number.length; j++) {
      for (let i = 0; i < alph.length; i++) {
        if (number[j] === alph[i]) {
          mark = true
          break
        } else {
          mark = false
          continue
        }
      }
      if (mark === true) {
        new_number += number[j]
      }
    }
    return new_number
  }

  const checkTimeDisabled = (checked: boolean) => {
    updateb24TimeState(checked)
    if (b24TimeState === false) {
      updateTimeState(true)
    } else {
      updateTimeState(false)
    }
  }

  const other_users: any[] = []
  if (all_users) {
    for (let i = 0; i < all_users.length; i++) {
      if (all_users[i]?.id !== user.id) {
        other_users.push(all_users[i])
      }
    }
  }

  const formikUser = useFormik<IBitrix24UserInServiceModel>({
    initialValues: Object.assign(Bitrix24UserInServiceInitValues, {
      id: user.id,
      is_active: user.is_active,
      bitrix24_user_id: user.bitrix24_user_id,
      internal_number: validateNumber(user.internal_number),
      external_number: validateNumber(user.external_number),
      fullname: user.fullname,
      bitrix24_manager_time: user.bitrix24_manager_time,
      smart_call: user.smart_call,
      smart_call_time: {
        mon_is_active: user.smart_call_time.mon_is_active,
        mon_start_time: user.smart_call_time.mon_start_time.slice(0, 5),
        mon_end_time: user.smart_call_time.mon_end_time.slice(0, 5),
        tue_is_active: user.smart_call_time.tue_is_active,
        tue_start_time: user.smart_call_time.tue_start_time.slice(0, 5),
        tue_end_time: user.smart_call_time.tue_end_time.slice(0, 5),
        wed_is_active: user.smart_call_time.wed_is_active,
        wed_start_time: user.smart_call_time.wed_start_time.slice(0, 5),
        wed_end_time: user.smart_call_time.wed_end_time.slice(0, 5),
        thu_is_active: user.smart_call_time.thu_is_active,
        thu_start_time: user.smart_call_time.thu_start_time.slice(0, 5),
        thu_end_time: user.smart_call_time.thu_end_time.slice(0, 5),
        fri_is_active: user.smart_call_time.fri_is_active,
        fri_start_time: user.smart_call_time.fri_start_time.slice(0, 5),
        fri_end_time: user.smart_call_time.fri_end_time.slice(0, 5),
        sat_is_active: user.smart_call_time.sat_is_active,
        sat_start_time: user.smart_call_time.sat_start_time.slice(0, 5),
        sat_end_time: user.smart_call_time.sat_end_time.slice(0, 5),
        sun_is_active: user.smart_call_time.sun_is_active,
        sun_start_time: user.smart_call_time.sun_start_time.slice(0, 5),
        sun_end_time: user.smart_call_time.sun_end_time.slice(0, 5),
      },
      chat: user.chat,
      auto_answer: user.auto_answer,
      multiple_registration: user.multiple_registration,
    }),
    validationSchema: getBitrix24UsersInServiceEditShema(personalData),

    onSubmit: async (values) => {
      const edit_user: IBitrix24UserInServiceModel = {
        id: user.id,
        is_active: values.is_active,
        bitrix24_user_id: user.bitrix24_user_id,
        internal_number: values.internal_number,
        external_number: values.external_number,
        fullname: values.fullname,
        bitrix24_manager_time: values.bitrix24_manager_time,
        smart_call: values.smart_call,
        smart_call_time: {
          mon_is_active: values.smart_call_time.mon_is_active,
          mon_start_time: values.smart_call_time.mon_start_time,
          mon_end_time: values.smart_call_time.mon_end_time,
          tue_is_active: values.smart_call_time.tue_is_active,
          tue_start_time: values.smart_call_time.tue_start_time,
          tue_end_time: values.smart_call_time.tue_end_time,
          wed_is_active: values.smart_call_time.wed_is_active,
          wed_start_time: values.smart_call_time.wed_start_time,
          wed_end_time: values.smart_call_time.wed_end_time,
          thu_is_active: values.smart_call_time.thu_is_active,
          thu_start_time: values.smart_call_time.thu_start_time,
          thu_end_time: values.smart_call_time.thu_end_time,
          fri_is_active: values.smart_call_time.fri_is_active,
          fri_start_time: values.smart_call_time.fri_start_time,
          fri_end_time: values.smart_call_time.fri_end_time,
          sat_is_active: values.smart_call_time.sat_is_active,
          sat_start_time: values.smart_call_time.sat_start_time,
          sat_end_time: values.smart_call_time.sat_end_time,
          sun_is_active: values.smart_call_time.sun_is_active,
          sun_start_time: values.smart_call_time.sun_start_time,
          sun_end_time: values.smart_call_time.sun_end_time,
        },
        chat: values.chat,
        auto_answer: values.auto_answer,
        multiple_registration: values.multiple_registration,
      }
      const {fullname, external_number, ...edit_user_without_personal_data} = edit_user
      const data = {
        id: appId!,
        settings: {
          bitrix24: {
            users_in_service: app?.settings.bitrix24.users_in_service,
            users_in_service_list: [personalData ? edit_user : edit_user_without_personal_data],
          },
        },
      }
      await updateApp(data as IAppsModel)
      updateUserState(false)
    },
  })

  return (
    <>
      {userState === false && (
        <tr role='row' className='text-gray-900 text-center'>
          <td role='cell' className='min-w-70px'>
            <div className='d-flex justify-content-end'>
              <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
                <input
                  className='form-check-input align-self-center'
                  type='checkbox'
                  id={'switchActive' + user.bitrix24_user_id}
                  checked={user.is_active}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-gray-900 text-center'
              value={user.bitrix24_user_id}
              disabled
              readOnly
            />
          </td>
          {personalData && (
            <td role='cell' className='min-w-150px pe-0 px-0'>
              <input
                className='form-control form-control-sm form-control-flush text-center'
                value={user.fullname}
                disabled
                readOnly
              />
            </td>
          )}
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              value={validateNumber(user.internal_number)}
              disabled
              readOnly
            />
          </td>
          {personalData && (
            <td role='cell' className='min-w-150px pe-0 px-0'>
              <input
                className='form-control form-control-sm form-control-flush text-center'
                value={validateNumber(user.external_number)}
                disabled
                readOnly
              />
            </td>
          )}
          <td role='cell' className='min-w-100px px-0 d-flex justify-content-center'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block me-2 align-self-center'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'userSmartCall' + user.bitrix24_user_id}
                checked={user.smart_call}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'userSmartCall' + user.bitrix24_user_id}></label>
            </div>
            <button type='button' className='btn btn-icon btn-light-primary btn-sm align-self-center' disabled>
              <KTSVG path='/media/icons/duotune/general/gen013.svg' className='align-self-center' />
            </button>
          </td>
          <td role='cell' className='min-w-100px px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'userB24Time' + user.bitrix24_user_id}
                checked={user.bitrix24_manager_time}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'userB24Time' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'userChat' + user.bitrix24_user_id}
                checked={user.chat}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'userChat' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'autoAnswer' + user.bitrix24_user_id}
                checked={user.auto_answer}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'autoAnswer' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'multRegistration' + user.bitrix24_user_id}
                checked={user.multiple_registration}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'multRegistration' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <button
              type='button'
              onClick={() => {
                updateUserState(true)
              }}
              className='btn btn-icon btn-light-primary btn-sm me-1 align-self-center'
            >
              <KTSVG path='/media/icons/duotune/art/art005.svg' className='align-self-center' />
            </button>
          </td>
        </tr>
      )}
      {userState === true && (
        <tr role='row' className='text-center'>
          <td className='d-none'>
            <form
              onSubmit={formikUser.handleSubmit}
              noValidate
              method='GET'
              id={'UserForm' + user?.bitrix24_user_id}
              className='form'
            ></form>
            <input type='hidden' {...formikUser.getFieldProps('id')} />
          </td>
          <td role='cell' className='min-w-70px'>
            <div className='d-flex justify-content-end'>
              <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
                <input
                  className='form-check-input align-self-center'
                  type='checkbox'
                  id={'switchEditActive' + user.bitrix24_user_id}
                  defaultChecked={user.is_active}
                  form={'Userform' + user?.bitrix24_user_id}
                  onClick={(e: any) => {
                    const numberInput = document.getElementById(
                      'editInternalNumber' + user.bitrix24_user_id
                    ) as HTMLInputElement
                    if (numberInput.value === '') {
                      e.target.checked = false
                      numberInput.classList.add('bg-light-danger')
                      numberInput.focus()
                    }
                  }}
                  {...formikUser.getFieldProps('is_active')}
                />
              </div>
            </div>
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              form={'Userform' + user?.bitrix24_user_id}
              {...formikUser.getFieldProps('bitrix24_user_id')}
              disabled
            />
          </td>
          {personalData && (
            <td role='cell' className='min-w-150px pe-0 px-0'>
              <input
                className='form-control form-control-sm form-control-flush text-center'
                {...formikUser.getFieldProps('fullname')}
                form={'UserForm' + user?.bitrix24_user_id}
              />
              {formikUser.touched.fullname && formikUser.errors.fullname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block text-danger'>{formikUser.errors.fullname}</div>
                </div>
              )}
            </td>
          )}
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              form={'Userform' + user?.bitrix24_user_id}
              id={'editInternalNumber' + user.bitrix24_user_id}
              onInput={(e: any) => {
                const activeSwitch = document.getElementById(
                  'switchEditActive' + user.bitrix24_user_id
                ) as HTMLInputElement
                let number = e.target.value
                const alph = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
                let mark = false
                for (let i = 0; i < alph.length; i++) {
                  if (number[number.length - 1] === alph[i]) {
                    mark = true
                    break
                  } else {
                    mark = false
                    continue
                  }
                }
                if (mark === false) {
                  e.target.value = number.slice(0, number.length - 1)
                }
                if (activeSwitch.checked === true && e.target.value === '') {
                  activeSwitch.click()
                  e.target.classList.add('bg-light-danger')
                  e.target.focus()
                } else if (e.target.value !== '') {
                  e.target.classList.remove('bg-light-danger')
                }
              }}
              {...formikUser.getFieldProps('internal_number')}
            />
          </td>
          {personalData && (
            <td role='cell' className='min-w-150px pe-0 px-0'>
              <input
                onInput={(e: any) => {
                  let number = e.target.value
                  const alph = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '+']
                  let mark = false
                  for (let i = 0; i < alph.length; i++) {
                    if (number[number.length - 1] === alph[i]) {
                      mark = true
                      break
                    } else {
                      mark = false
                      continue
                    }
                  }
                  if (mark === false) {
                    e.target.value = number.slice(0, number.length - 1)
                  }
                }}
                className='form-control form-control-sm form-control-flush text-center'
                form={'Userform' + user?.bitrix24_user_id}
                {...formikUser.getFieldProps('external_number')}
              />
            </td>
          )}
          <td role='cell' className='min-w-100px px-0 d-flex justify-content-center'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block me-2 align-self-center'>
              <input
                form={'Userform' + user?.bitrix24_user_id}
                className='form-check-input'
                type='checkbox'
                id={'userEditSmartCall' + user.bitrix24_user_id}
                defaultChecked={user.smart_call}
                onClick={(e: any) => {
                  checkSMCallDisabled(e.target.checked)
                }}
                {...formikUser.getFieldProps('smart_call')}
              />
              <label className='form-check-label' htmlFor={'userEditSmartCall' + user.bitrix24_user_id}></label>
            </div>
            <button
              type='button'
              disabled={timeState}
              className='btn btn-icon btn-light-primary btn-sm align-self-center'
              data-bs-toggle='modal'
              data-bs-target={'#SmartCallTimeModal' + user.id}
            >
              <KTSVG path='/media/icons/duotune/general/gen013.svg' className='align-self-center' />
            </button>
          </td>
          <td role='cell' className='min-w-100px px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                form={'Userform' + user?.bitrix24_user_id}
                id={'userEditB24Time' + user.bitrix24_user_id}
                defaultChecked={user.bitrix24_manager_time}
                disabled={b24TimeDisabledState}
                onClick={(e: any) => {
                  checkTimeDisabled(e.target.checked)
                }}
                {...formikUser.getFieldProps('bitrix24_manager_time')}
              />
              <label className='form-check-label' htmlFor={'userEditB24Time' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                form={'Userform' + user?.bitrix24_user_id}
                id={'userEditChat' + user.bitrix24_user_id}
                defaultChecked={user.chat}
                {...formikUser.getFieldProps('chat')}
              />
              <label className='form-check-label' htmlFor={'userEditChat' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                form={'Userform' + user?.bitrix24_user_id}
                id={'editAutoAnswer' + user.bitrix24_user_id}
                defaultChecked={user.auto_answer}
                onClick={(e: any) => {
                  const multRegistr = document.getElementById(
                    'editMultRegistration' + user.bitrix24_user_id
                  ) as HTMLInputElement
                  if (e.target.checked === true && multRegistr.checked === true) {
                    multRegistr.click()
                  }
                }}
                {...formikUser.getFieldProps('auto_answer')}
              />
              <label className='form-check-label' htmlFor={'editAutoAnswer' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-70px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                form={'Userform' + user?.bitrix24_user_id}
                id={'editMultRegistration' + user.bitrix24_user_id}
                defaultChecked={user.multiple_registration}
                onClick={(e: any) => {
                  const autoAnswer = document.getElementById(
                    'editAutoAnswer' + user.bitrix24_user_id
                  ) as HTMLInputElement
                  if (e.target.checked === true && autoAnswer.checked === true) {
                    autoAnswer.click()
                  }
                }}
                {...formikUser.getFieldProps('multiple_registration')}
              />
              <label className='form-check-label' htmlFor={'editMultRegistration' + user.bitrix24_user_id}></label>
            </div>
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <button
              form={'UserForm' + user?.bitrix24_user_id}
              type='submit'
              title='Save'
              className='btn btn-icon btn-light-success btn-active-color-light btn-sm me-1 align-self-center'
              disabled={isLoading}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen037.svg' className='align-self-center' />}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <span className='spinner-border spinner-border-sm align-middle'></span>
                </span>
              )}
            </button>
            <button
              form={'UserForm' + user?.bitrix24_user_id}
              className='btn btn-icon btn-light-primary btn-active-color-light btn-sm me-1 align-self-center'
              title='Close'
              onClick={() => {
                updateUserState(false)
                formikUser.resetForm()
              }}
              disabled={isLoading}
              hidden={isLoading}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen034.svg' className='align-self-center' />}
            </button>

            <div className='modal fade' tabIndex={-1} id={'SmartCallTimeModal' + user.id}>
              <div className='modal-dialog modal-dialog-centered mw-400px'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h2>{intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.TITTLE'})}</h2>
                    <div
                      className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                    >
                      <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
                    </div>
                  </div>
                  <div className='modal-body'>
                    <table className='table align-middle table-row-dashed fs-7 gy-5 dataTable no-footer'>
                      <thead>
                        <tr className='text-muted fs-9'>
                          <th role='columnheader' className='min-w-70px'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.ACTIVE'})}
                          </th>
                          <th role='columnheader' className='min-w-70px'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.DAY'})}
                          </th>
                          <th role='columnheader' className='min-w-150px'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.STARTTIME'})} —{' '}
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.ENDTIME'})}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'MonActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.mon_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.mon_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.MONDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeMon' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.mon_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeMon' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.mon_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'TueActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.tue_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.tue_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.TUESDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeTue' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.tue_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeTue' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.tue_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'WedActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.wed_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.wed_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.WEDNESDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeWed' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.wed_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeWed' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.wed_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'ThuActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.thu_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.thu_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.THURSDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeThu' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.thu_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeThu' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.thu_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'FriActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.fri_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.fri_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.FRIDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeFri' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.fri_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeFri' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.fri_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'SatActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.sat_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.sat_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.SATURDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeSat' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.sat_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeSat' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.sat_end_time')}
                            />
                          </td>
                        </tr>
                        <tr role='row' className='text-center'>
                          <td role='cell' className='min-w-70px pe-0'>
                            <div className='d-flex justify-content-center'>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input align-self-center'
                                  type='checkbox'
                                  id={'SunActive' + user.bitrix24_user_id}
                                  defaultChecked={user.smart_call_time.sun_is_active}
                                  form={'Userform' + user?.bitrix24_user_id}
                                  {...formikUser.getFieldProps('smart_call_time.sun_is_active')}
                                />
                              </div>
                            </div>
                          </td>
                          <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                            {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.SUNDAY'})}
                          </td>
                          <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editstartTimeSun' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.sun_start_time')}
                            />
                            —
                            <input
                              className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                              id={'editendTimeSun' + user?.bitrix24_user_id}
                              form={'Userform' + user?.bitrix24_user_id}
                              {...formikUser.getFieldProps('smart_call_time.sun_end_time')}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className='modal-footer'>
                    <div className='card-footer d-flex justify-content-end py-2 px-1' style={{border: 'none'}}>
                      <button
                        type='button'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                        className='btn btn-sm btn-primary me-3 text-uppercase'
                      >
                        ok
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary align-self-center'
                        id='close-modal'
                        data-bs-dismiss='modal'
                        aria-label='Close'
                      >
                        {intl.formatMessage({id: 'BUTTON.CLOSE'})}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export {OneUserInServiceBitrix24}
